import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Building2, User, Mail, Phone, MapPin, FileText, 
  CheckCircle2, ArrowRight, ArrowLeft, Loader2, X
} from 'lucide-react'
import { GetCountries, GetState, GetCity } from 'react-country-state-city'
import type { Country, State, City } from 'react-country-state-city'
import { API_CONFIG } from '../config/settings'

interface FormData {
  // Step 1: Basic Information
  username: string
  email: string
  phone: string
  
  // Step 2: Business Information
  name: string
  country: string
  countryCode: string
  state: string
  stateCode: string
  city: string
  zip_code: string
  address: string
  details: string
}

const VendorSignup = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [vendorInfo, setVendorInfo] = useState<{username: string, email: string, emailSent: boolean} | null>(null)
  const [resendingEmail, setResendingEmail] = useState(false)
  
  const [formData, setFormData] = useState<FormData>({
    username: '',
    email: '',
    phone: '',
    name: '',
    country: '',
    countryCode: '',
    city: '',
    state: '',
    stateCode: '',
    zip_code: '',
    address: '',
    details: ''
  })

  const [errors, setErrors] = useState<Partial<FormData>>({})
  
  // Username availability check
  const [usernameChecking, setUsernameChecking] = useState(false)
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null)
  const [usernameMessage, setUsernameMessage] = useState<string>('')
  
  // Email availability check
  const [emailChecking, setEmailChecking] = useState(false)
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null)
  const [emailMessage, setEmailMessage] = useState<string>('')
  
  // Location dropdown states
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null)
  const [selectedState, setSelectedState] = useState<State | null>(null)
  const [selectedCity, setSelectedCity] = useState<City | null>(null)
  const [countries, setCountries] = useState<Country[]>([])
  const [states, setStates] = useState<State[]>([])
  const [cities, setCities] = useState<City[]>([])
  const [pincodeOptions, setPincodeOptions] = useState<string[]>([])
  const [pincodeLoading, setPincodeLoading] = useState(false)
  
  // Load countries on mount
  useEffect(() => {
    GetCountries().then(setCountries).catch(console.error)
  }, [])
  
  // Load states when country is selected
  useEffect(() => {
    if (selectedCountry) {
      GetState(selectedCountry.id).then(setStates).catch(console.error)
    } else {
      setStates([])
    }
  }, [selectedCountry])
  
  // Load cities when state is selected
  useEffect(() => {
    if (selectedState && selectedCountry) {
      GetCity(selectedCountry.id, selectedState.id).then(setCities).catch(console.error)
    } else {
      setCities([])
    }
  }, [selectedState, selectedCountry])
  
  // Fetch pincodes when city is selected (for India)
  useEffect(() => {
    if (selectedCity && selectedCountry?.iso2 === 'IN') {
      fetchPincodes(selectedCity.name, selectedState?.name || '')
    } else {
      setPincodeOptions([])
    }
  }, [selectedCity, selectedState, selectedCountry])
  
  const fetchPincodes = async (cityName: string, stateName: string) => {
    setPincodeLoading(true)
    try {
      // Using India Post API to search by city name
      const searchResponse = await fetch(`https://api.postalpincode.in/postoffice/${encodeURIComponent(cityName)}`)
      const searchData = await searchResponse.json()
      
      if (searchData && searchData[0] && searchData[0].Status === 'Success' && searchData[0].PostOffice) {
        // Filter by state if available
        let postOffices = searchData[0].PostOffice
        if (stateName) {
          postOffices = postOffices.filter((po: any) => 
            po.State.toLowerCase().includes(stateName.toLowerCase()) || 
            stateName.toLowerCase().includes(po.State.toLowerCase())
          )
        }
        
        const pincodes = postOffices.map((po: any) => po.Pincode)
        const uniquePincodes = [...new Set(pincodes)].sort()
        setPincodeOptions(uniquePincodes)
      } else {
        setPincodeOptions([])
      }
    } catch (error) {
      console.error('Error fetching pincodes:', error)
      setPincodeOptions([])
    } finally {
      setPincodeLoading(false)
    }
  }

  const validateStep1 = (): boolean => {
    const newErrors: Partial<FormData> = {}
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required'
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters'
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores'
    } else if (usernameAvailable === false) {
      newErrors.username = 'Username is already taken'
    } else if (usernameChecking) {
      newErrors.username = 'Please wait while we check username availability'
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    } else if (emailAvailable === false) {
      newErrors.email = 'Email is already registered'
    } else if (emailChecking) {
      newErrors.email = 'Please wait while we check email availability'
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required'
    } else if (!/^[\d\s\-\+\(\)]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateStep2 = (): boolean => {
    const newErrors: Partial<FormData> = {}
    
    if (!formData.name.trim()) {
      newErrors.name = 'Business name is required'
    }
    
    if (!formData.country.trim()) {
      newErrors.country = 'Country is required'
    }
    
    if (!formData.state.trim()) {
      newErrors.state = 'State is required'
    }
    
    if (!formData.city.trim()) {
      newErrors.city = 'City is required'
    }
    
    if (!formData.zip_code.trim()) {
      newErrors.zip_code = 'Pincode/ZIP code is required'
    } else if (selectedCountry?.iso2 === 'IN' && formData.zip_code.length !== 6) {
      newErrors.zip_code = 'Indian pincode must be 6 digits'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    if (currentStep === 1) {
      if (validateStep1()) {
        setCurrentStep(2)
        setError(null)
      }
    } else if (currentStep === 2) {
      if (validateStep2()) {
        handleSubmit()
      }
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
      setError(null)
    }
  }

  const handleSubmit = async () => {
    if (!validateStep2()) return
    
    setLoading(true)
    setError(null)
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/vendor/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          phone: formData.phone,
          name: formData.name,
          country: formData.country || null,
          city: formData.city || null,
          state: formData.state || null,
          zip_code: formData.zip_code || null,
          address: formData.address || null,
          details: formData.details || null
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create vendor account')
      }

      // Store vendor info for resend email
      setVendorInfo({
        username: data.username || formData.username,
        email: data.email || formData.email,
        emailSent: data.email_sent || false
      })
      setSuccess(true)
    } catch (err: any) {
      console.error('Signup error:', err)
      setError(err.message || 'Failed to create vendor account. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Debounce timers for username and email checks
  const usernameCheckTimerRef = React.useRef<NodeJS.Timeout | null>(null)
  const emailCheckTimerRef = React.useRef<NodeJS.Timeout | null>(null)
  
  const checkUsernameAvailability = async (username: string) => {
    if (!username || username.length < 3) {
      setUsernameAvailable(null)
      setUsernameMessage('')
      return
    }
    
    // Validate format
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setUsernameAvailable(false)
      setUsernameMessage('Username can only contain letters, numbers, and underscores')
      return
    }
    
    setUsernameChecking(true)
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/vendor/check-username?username=${encodeURIComponent(username)}`
      )
      const data = await response.json()
      
      setUsernameAvailable(data.available)
      setUsernameMessage(data.message || '')
    } catch (error) {
      console.error('Error checking username:', error)
      setUsernameAvailable(null)
      setUsernameMessage('')
    } finally {
      setUsernameChecking(false)
    }
  }
  
  const checkEmailAvailability = async (email: string) => {
    if (!email || email.length < 5) {
      setEmailAvailable(null)
      setEmailMessage('')
      return
    }
    
    // Validate email format
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailPattern.test(email)) {
      setEmailAvailable(false)
      setEmailMessage('Please enter a valid email address')
      return
    }
    
    setEmailChecking(true)
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/vendor/check-email?email=${encodeURIComponent(email)}`
      )
      const data = await response.json()
      
      setEmailAvailable(data.available)
      setEmailMessage(data.message || '')
    } catch (error) {
      console.error('Error checking email:', error)
      setEmailAvailable(null)
      setEmailMessage('')
    } finally {
      setEmailChecking(false)
    }
  }
  
  const updateFormData = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
    
    // Check username availability with debounce
    if (field === 'username') {
      // Clear previous timer
      if (usernameCheckTimerRef.current) {
        clearTimeout(usernameCheckTimerRef.current)
      }
      
      // Reset availability state
      setUsernameAvailable(null)
      setUsernameMessage('')
      
      // Set new timer for debounce (500ms delay)
      usernameCheckTimerRef.current = setTimeout(() => {
        checkUsernameAvailability(value.trim())
      }, 500)
    }
    
    // Check email availability with debounce
    if (field === 'email') {
      // Clear previous timer
      if (emailCheckTimerRef.current) {
        clearTimeout(emailCheckTimerRef.current)
      }
      
      // Reset availability state
      setEmailAvailable(null)
      setEmailMessage('')
      
      // Set new timer for debounce (500ms delay)
      emailCheckTimerRef.current = setTimeout(() => {
        checkEmailAvailability(value.trim())
      }, 500)
    }
  }
  
  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (usernameCheckTimerRef.current) {
        clearTimeout(usernameCheckTimerRef.current)
      }
      if (emailCheckTimerRef.current) {
        clearTimeout(emailCheckTimerRef.current)
      }
    }
  }, [])

  const handleResendEmail = async () => {
    if (!vendorInfo) return
    
    setResendingEmail(true)
    setError(null)
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/vendor/resend-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: vendorInfo.email,
          username: vendorInfo.username
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to resend email')
      }

      if (data.email_sent) {
        setVendorInfo({ ...vendorInfo, emailSent: true })
        alert('Email sent successfully! Please check your inbox.')
      } else {
        alert(`Email sending failed: ${data.email_error || 'Unknown error'}`)
      }
    } catch (err: any) {
      console.error('Resend email error:', err)
      alert(err.message || 'Failed to resend email. Please try again.')
    } finally {
      setResendingEmail(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Created Successfully!</h2>
            <p className="text-gray-600 mb-4">
              Your vendor account has been created.
            </p>
            
            {vendorInfo && (
              <div className="mb-6">
                {vendorInfo.emailSent ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-center gap-2 text-green-800 mb-2">
                      <CheckCircle2 className="h-5 w-5" />
                      <p className="text-sm font-semibold">Email Sent Successfully!</p>
                    </div>
                    <p className="text-sm text-green-700">
                      Please check your inbox at <strong>{vendorInfo.email}</strong> for login credentials.
                    </p>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-center gap-2 text-yellow-800 mb-2">
                      <Mail className="h-5 w-5" />
                      <p className="text-sm font-semibold">Email Not Received?</p>
                    </div>
                    <p className="text-sm text-yellow-700 mb-3">
                      We couldn't send the email. Click the button below to resend.
                    </p>
                    <button
                      onClick={handleResendEmail}
                      disabled={resendingEmail}
                      className="w-full px-4 py-2 bg-yellow-600 text-white rounded-lg font-semibold hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {resendingEmail ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="h-4 w-4" />
                          Resend Email
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800 mb-2">
                <strong>Login URL:</strong>
              </p>
              <a 
                href="/vendor/login" 
                className="text-blue-600 hover:underline break-all"
              >
                /vendor/login
              </a>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/')}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                Go to Home
              </button>
              {vendorInfo && !vendorInfo.emailSent && (
                <button
                  onClick={handleResendEmail}
                  disabled={resendingEmail}
                  className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {resendingEmail ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4" />
                      Resend
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-full mb-4">
            <Building2 className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Become a Vendor</h1>
          <p className="text-gray-600">Join AonlaStay and start hosting guests today</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold ${
                currentStep >= 1 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                1
              </div>
              <div className={`flex-1 h-1 mx-2 ${
                currentStep >= 2 ? 'bg-primary-600' : 'bg-gray-200'
              }`} />
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold ${
                currentStep >= 2 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                2
              </div>
            </div>
          </div>
          <div className="flex justify-between mt-2">
            <span className={`text-sm font-medium ${currentStep >= 1 ? 'text-primary-600' : 'text-gray-500'}`}>
              Basic Information
            </span>
            <span className={`text-sm font-medium ${currentStep >= 2 ? 'text-primary-600' : 'text-gray-500'}`}>
              Business Details
            </span>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <User className="h-6 w-6 text-primary-600" />
                  Basic Information
                </h2>
                <p className="text-gray-600 mb-6">Let's start with your account details</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Username <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => updateFormData('username', e.target.value)}
                    className={`w-full px-4 py-3 pr-10 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.username 
                        ? 'border-red-300' 
                        : usernameAvailable === true 
                        ? 'border-green-300' 
                        : usernameAvailable === false 
                        ? 'border-red-300' 
                        : 'border-gray-200'
                    }`}
                    placeholder="Choose a unique username"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
                    {usernameChecking ? (
                      <Loader2 className="h-5 w-5 text-primary-600 animate-spin" />
                    ) : usernameAvailable === true ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    ) : usernameAvailable === false ? (
                      <X className="h-5 w-5 text-red-600" />
                    ) : null}
                  </div>
                </div>
                {usernameMessage && (
                  <p className={`mt-1 text-sm ${
                    usernameAvailable === true 
                      ? 'text-green-600' 
                      : usernameAvailable === false 
                      ? 'text-red-600' 
                      : 'text-gray-500'
                  }`}>
                    {usernameMessage}
                  </p>
                )}
                {errors.username && (
                  <p className="mt-1 text-sm text-red-600">{errors.username}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Mail className="h-4 w-4 inline mr-1" />
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateFormData('email', e.target.value)}
                    className={`w-full px-4 py-3 pr-10 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.email 
                        ? 'border-red-300' 
                        : emailAvailable === true 
                        ? 'border-green-300' 
                        : emailAvailable === false 
                        ? 'border-red-300' 
                        : 'border-gray-200'
                    }`}
                    placeholder="your.email@example.com"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
                    {emailChecking ? (
                      <Loader2 className="h-5 w-5 text-primary-600 animate-spin" />
                    ) : emailAvailable === true ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    ) : emailAvailable === false ? (
                      <X className="h-5 w-5 text-red-600" />
                    ) : null}
                  </div>
                </div>
                {emailMessage && (
                  <p className={`mt-1 text-sm ${
                    emailAvailable === true 
                      ? 'text-green-600' 
                      : emailAvailable === false 
                      ? 'text-red-600' 
                      : 'text-gray-500'
                  }`}>
                    {emailMessage}
                  </p>
                )}
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Phone className="h-4 w-4 inline mr-1" />
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateFormData('phone', e.target.value)}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.phone ? 'border-red-300' : 'border-gray-200'
                  }`}
                  placeholder="+91 9876543210"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Business Information */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Building2 className="h-6 w-6 text-primary-600" />
                  Business Details
                </h2>
                <p className="text-gray-600 mb-6">Tell us about your business</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Business Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => updateFormData('name', e.target.value)}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    errors.name ? 'border-red-300' : 'border-gray-200'
                  }`}
                  placeholder="Your business or property name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <MapPin className="h-4 w-4 inline mr-1" />
                  Country <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedCountry?.id || ''}
                  onChange={(e) => {
                    const country = countries.find(c => c.id === Number(e.target.value))
                    setSelectedCountry(country || null)
                    setSelectedState(null)
                    setSelectedCity(null)
                    updateFormData('country', country?.name || '')
                    updateFormData('countryCode', country?.iso2 || '')
                    updateFormData('state', '')
                    updateFormData('stateCode', '')
                    updateFormData('city', '')
                    updateFormData('zip_code', '')
                  }}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                >
                  <option value="">Select Country</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
                {errors.country && (
                  <p className="mt-1 text-sm text-red-600">{errors.country}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedState?.id || ''}
                  onChange={(e) => {
                    const state = states.find(s => s.id === Number(e.target.value))
                    setSelectedState(state || null)
                    setSelectedCity(null)
                    updateFormData('state', state?.name || '')
                    updateFormData('stateCode', state?.iso2 || '')
                    updateFormData('city', '')
                    updateFormData('zip_code', '')
                  }}
                  disabled={!selectedCountry}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white ${
                    !selectedCountry ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'border-gray-200'
                  }`}
                >
                  <option value="">{selectedCountry ? 'Select State' : 'Select Country first'}</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
                {errors.state && (
                  <p className="mt-1 text-sm text-red-600">{errors.state}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedCity?.name || ''}
                  onChange={(e) => {
                    const city = cities.find(c => c.name === e.target.value)
                    setSelectedCity(city || null)
                    updateFormData('city', city?.name || '')
                    updateFormData('zip_code', '')
                  }}
                  disabled={!selectedState}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white ${
                    !selectedState ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'border-gray-200'
                  }`}
                >
                  <option value="">{selectedState ? 'Select City' : 'Select State first'}</option>
                  {cities.map((city) => (
                    <option key={city.name} value={city.name}>
                      {city.name}
                    </option>
                  ))}
                </select>
                {errors.city && (
                  <p className="mt-1 text-sm text-red-600">{errors.city}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Pincode / ZIP Code <span className="text-red-500">*</span>
                </label>
                {pincodeLoading ? (
                  <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
                    <span className="text-sm text-gray-600">Loading pincodes...</span>
                  </div>
                ) : pincodeOptions.length > 0 ? (
                  <select
                    value={formData.zip_code}
                    onChange={(e) => updateFormData('zip_code', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                  >
                    <option value="">Select Pincode</option>
                    {pincodeOptions.map((pincode) => (
                      <option key={pincode} value={pincode}>
                        {pincode}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.zip_code}
                    onChange={(e) => updateFormData('zip_code', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.zip_code ? 'border-red-300' : 'border-gray-200'
                    }`}
                    placeholder={selectedCountry?.iso2 === 'IN' ? 'Enter 6-digit pincode' : 'Enter ZIP code'}
                    maxLength={selectedCountry?.iso2 === 'IN' ? 6 : 10}
                  />
                )}
                {errors.zip_code && (
                  <p className="mt-1 text-sm text-red-600">{errors.zip_code}</p>
                )}
                {selectedCountry?.iso2 === 'IN' && selectedCity && pincodeOptions.length === 0 && !pincodeLoading && (
                  <p className="mt-1 text-xs text-gray-500">Enter your 6-digit pincode manually</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => updateFormData('address', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Street address, building number, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <FileText className="h-4 w-4 inline mr-1" />
                  Additional Details
                </label>
                <textarea
                  value={formData.details}
                  onChange={(e) => updateFormData('details', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Tell us more about your business, services, or property..."
                />
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={handleBack}
              disabled={currentStep === 1 || loading}
              className="flex items-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <button
              onClick={handleNext}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : currentStep === 2 ? (
                <>
                  Submit
                  <CheckCircle2 className="h-4 w-4" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-6 text-center text-sm text-gray-600">
          <p>Already have an account? <a href="/vendor/login" className="text-primary-600 hover:underline">Login here</a></p>
        </div>
      </div>
    </div>
  )
}

export default VendorSignup

